/**
 * The arithmetic behind the hand-drawn charts.
 *
 * Kept apart from the components for one reason: this is the part that is worth
 * testing. A path string is hard to assert against and easy to eyeball; a scale
 * that quietly tops out below the tallest bar is the reverse, and it is the bug
 * that makes a chart lie.
 *
 * Every function here works in the chart's own coordinate space, which is the
 * viewBox. Nothing here knows about pixels, because nothing about these charts
 * does: they scale with their container and the browser does the conversion.
 */

export interface Point {
  x: number
  y: number
}

/**
 * The frame the axed charts are drawn in, shared so that a bar chart and a line
 * chart stacked on a dashboard line up with each other. They did not in an
 * earlier draft, where each file carried its own gutters and the two y axes sat
 * four units apart, which reads as one of them being slightly broken.
 *
 * These are viewBox units and not pixels. The width is chosen close to the
 * width these charts are usually rendered at, because the viewBox scales type
 * along with everything else: a plot drawn 320 units wide and displayed at 640
 * shows its 12 unit labels at 24 pixels.
 */
export const plot = {
  width: 640,
  height: 260,
  left: 48,
  right: 8,
  top: 12,
  bottom: 32,
} as const

export const plotWidth = plot.width - plot.left - plot.right
export const plotHeight = plot.height - plot.top - plot.bottom
/** The y coordinate of zero. Every bar and every area is measured from here. */
export const plotBaseline = plot.top + plotHeight

/**
 * Two decimal places. Path data is text, and a chart with sixty points at
 * fifteen significant figures each is several kilobytes of markup that renders
 * identically to this.
 */
function trim(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * An axis that tops out at a round number.
 *
 * Scaling to the exact maximum gives ticks like 4,173 and 8,346, which nobody
 * can read at a glance and which imply a precision the counts do not have. This
 * walks up to the next 1, 2, 2.5 or 5 times a power of ten, so the labels are
 * numbers a person would have chosen.
 *
 * A max of zero returns a scale of 0 to 1 rather than dividing by nothing. An
 * empty week is a real thing to draw and it should draw as a flat floor, not as
 * a chart full of NaN.
 */
export function niceScale(max: number, tickCount = 4): { max: number; ticks: number[] } {
  if (!Number.isFinite(max) || max <= 0) return { max: 1, ticks: [0, 1] }

  const rough = max / tickCount
  const magnitude = 10 ** Math.floor(Math.log10(rough))
  const normalised = rough / magnitude
  const factor = [1, 2, 2.5, 5, 10].find((candidate) => normalised <= candidate) ?? 10
  const step = factor * magnitude
  const top = Math.ceil(max / step) * step

  const ticks: number[] = []
  // Multiplied out rather than accumulated. Adding 0.1 to itself thirty times
  // produces 3.0000000000000004, and that ends up printed on an axis.
  for (let index = 0; index * step <= top + step / 2; index += 1) {
    ticks.push(Number((index * step).toFixed(10)))
  }
  return { max: top, ticks }
}

export function linePath(points: readonly Point[]): string {
  if (points.length === 0) return ''
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${trim(point.x)} ${trim(point.y)}`)
    .join(' ')
}

/** The line, closed down to a baseline, for a filled area under a series. */
export function areaPath(points: readonly Point[], baseline: number): string {
  const first = points[0]
  const last = points[points.length - 1]
  if (!first || !last) return ''
  const floor = trim(baseline)
  return `${linePath(points)} L${trim(last.x)} ${floor} L${trim(first.x)} ${floor} Z`
}

/**
 * Zero degrees is twelve o'clock and angles run clockwise, which is how a
 * person reads a pie and is not how the maths works by default. Converting here
 * once means no component has to remember to subtract ninety degrees.
 */
export function polar(centreX: number, centreY: number, radius: number, degrees: number): Point {
  const radians = ((degrees - 90) * Math.PI) / 180
  return {
    x: centreX + radius * Math.cos(radians),
    y: centreY + radius * Math.sin(radians),
  }
}

/**
 * One ring segment.
 *
 * The sweep is capped just under a full turn. An SVG arc that ends where it
 * began draws nothing at all, so a category holding 100% of the total would
 * vanish, and a single category holding everything is exactly what a new
 * project's first chart looks like. The missing hundredth of a degree is around
 * a twentieth of a pixel and closes visually.
 */
export function donutSlicePath(
  centreX: number,
  centreY: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
): string {
  const sweep = Math.min(endAngle - startAngle, 359.99)
  if (sweep <= 0) return ''
  const end = startAngle + sweep
  const largeArc = sweep > 180 ? 1 : 0

  const outerStart = polar(centreX, centreY, outerRadius, startAngle)
  const outerEnd = polar(centreX, centreY, outerRadius, end)
  const innerEnd = polar(centreX, centreY, innerRadius, end)
  const innerStart = polar(centreX, centreY, innerRadius, startAngle)

  return [
    `M${trim(outerStart.x)} ${trim(outerStart.y)}`,
    `A${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${trim(outerEnd.x)} ${trim(outerEnd.y)}`,
    `L${trim(innerEnd.x)} ${trim(innerEnd.y)}`,
    `A${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${trim(innerStart.x)} ${trim(innerStart.y)}`,
    'Z',
  ].join(' ')
}

/**
 * Evenly spaced slots across a width, with a gap between each.
 *
 * `padding` is the share of one slot given up to space, so the bars stay
 * proportionally spaced whether there are four categories or forty.
 */
export function bands(
  count: number,
  start: number,
  width: number,
  padding = 0.25,
): Array<{ start: number; width: number; centre: number }> {
  if (count <= 0) return []
  const slot = width / count
  const bar = slot * (1 - padding)
  const offset = (slot - bar) / 2
  return Array.from({ length: count }, (_, index) => {
    const left = start + slot * index + offset
    return { start: trim(left), width: trim(bar), centre: trim(left + bar / 2) }
  })
}

/**
 * Thins a list of tick labels down to at most `keep` of them, always keeping
 * the first and last. Overlapping axis labels are worse than missing ones: a
 * label nobody can read is noise, and the reader cannot tell which bar it
 * belongs to.
 */
export function thinLabels<T>(items: readonly T[], keep: number): Array<T | null> {
  if (items.length <= keep) return [...items]
  const stride = Math.ceil(items.length / keep)
  return items.map((item, index) =>
    index === 0 || index === items.length - 1 || index % stride === 0 ? item : null,
  )
}

export function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0)
}
